/**
 * Data Explorer API surface — RPC reader + data_views CRUD + form catalogue.
 *
 * The grid reader resolves the full DataViewSpec client-side; only the narrow,
 * server-validated query (form id + field keys + typed filters/sort) crosses to
 * the query_form_view RPC. RLS on submissions/records governs visibility.
 */
import { supabase } from '@/lib/supabase/client'
import {
  fetchFormVersion,
  fetchFormGroups,
} from '@/features/form-builder/api/form-builder-api'
import { buildFormCatalog, type FormCatalog } from '../lib/column-catalog'
import {
  resolveQueryArgs,
  resolveRecordQueryArgs,
} from '../lib/spec-resolver'
import type { QueryFormViewResult, RecordViewResult } from '../lib/spec-resolver'
import type {
  DataViewSpec,
  DataViewRow,
  ViewVisibility,
} from '../types/data-view-spec'

// ─── Grid reader ────────────────────────────────────────────────────────────

export async function queryFormView(
  spec: DataViewSpec,
  page: number,
  pageSize: number,
): Promise<QueryFormViewResult> {
  const args = resolveQueryArgs(spec)
  if (!args.formId) {
    return { layout: args.layout, total: 0, rows: [] }
  }
  const { data, error } = await supabase.rpc('query_form_view', {
    p_form_id: args.formId,
    p_field_keys: args.fieldKeys,
    p_filters: args.filters,
    p_sort_key: args.sortKey,
    p_sort_dir: args.sortDir,
    p_layout: args.layout,
    p_include_deleted: args.includeDeleted,
    p_limit: pageSize,
    p_offset: page * pageSize,
    p_search: args.search,
  })
  if (error) throw error
  return (data as unknown as QueryFormViewResult) ?? { layout: args.layout, total: 0, rows: [] }
}

// ─── Form catalogue (column metadata) ──────────────────────────────────────

export interface ExplorerForm {
  id: string
  title: string
  instanceType: 'single' | 'repeatable'
}

export interface ExplorerGroup {
  id: string
  title: string
  icon: string
  forms: ExplorerForm[]
}

/** Published forms only, grouped by form group — powers the view sidebar tree. */
export async function fetchExplorerForms(projectId: string): Promise<ExplorerGroup[]> {
  const groups = await fetchFormGroups(projectId)
  return groups
    .map((g) => ({
      id: g.id,
      title: g.title,
      icon: g.icon,
      forms: g.forms
        .filter((f) => f.isPublished && f.currentVersionId)
        .map((f) => ({ id: f.id, title: f.title, instanceType: f.instanceType })),
    }))
    .filter((g) => g.forms.length > 0)
}

export interface FormCatalogResult {
  catalog: FormCatalog
  formTitle: string
  instanceType: 'single' | 'repeatable'
}

export async function fetchFormCatalog(formId: string): Promise<FormCatalogResult> {
  const v = await fetchFormVersion(formId)
  return {
    catalog: buildFormCatalog(formId, v.definition),
    formTitle: v.formTitle,
    instanceType: v.instanceType as 'single' | 'repeatable',
  }
}

export interface MultiCatalogResult {
  catalogs: Record<string, FormCatalog>
  titles: Record<string, string>
  instanceTypes: Record<string, 'single' | 'repeatable'>
}

/** Catalogues for several forms at once — powers record-centric (multi-form) views. */
export async function fetchFormCatalogs(formIds: string[]): Promise<MultiCatalogResult> {
  const results = await Promise.all(formIds.map((id) => fetchFormCatalog(id).then((r) => [id, r] as const)))
  const out: MultiCatalogResult = { catalogs: {}, titles: {}, instanceTypes: {} }
  for (const [id, r] of results) {
    out.catalogs[id] = r.catalog
    out.titles[id] = r.formTitle
    out.instanceTypes[id] = r.instanceType
  }
  return out
}

// ─── Record-centric reader ──────────────────────────────────────────────────

export async function queryRecordView(
  spec: DataViewSpec,
  projectId: string,
  page: number,
  pageSize: number,
): Promise<RecordViewResult> {
  const args = resolveRecordQueryArgs(spec)
  if (args.forms.length === 0) return { total: 0, rows: [] }
  const { data, error } = await supabase.rpc('query_record_view', {
    p_project_id: projectId,
    p_forms: args.forms,
    p_filters: args.filters,
    p_sort_key: args.sortKey,
    p_sort_dir: args.sortDir,
    p_include_deleted: args.includeDeleted,
    p_limit: pageSize,
    p_offset: page * pageSize,
    p_search: args.search,
  })
  if (error) throw error
  return (data as unknown as RecordViewResult) ?? { total: 0, rows: [] }
}

// ─── data_views CRUD ─────────────────────────────────────────────────────────

export async function fetchDataViews(projectId: string): Promise<DataViewRow[]> {
  const { data, error } = await supabase
    .from('data_views')
    .select('*, owner:user_profiles!data_views_owner_id_fkey(full_name)')
    .eq('project_id', projectId)
    .eq('is_deleted', false)
    .order('name')
  if (error) throw error
  return (data ?? []).map((r: Record<string, unknown>) => ({
    id: r.id as string,
    project_id: r.project_id as string,
    name: r.name as string,
    description: (r.description as string) ?? null,
    owner_id: r.owner_id as string,
    visibility: r.visibility as ViewVisibility,
    spec: r.spec as DataViewSpec,
    is_default: r.is_default as boolean,
    default_for_form_id: (r.default_for_form_id as string) ?? null,
    version: r.version as number,
    created_at: r.created_at as string,
    updated_at: r.updated_at as string,
    owner_name: (r.owner as { full_name?: string } | null)?.full_name ?? '—',
  }))
}

export interface SaveDataViewInput {
  projectId: string
  ownerId: string
  name: string
  description?: string
  visibility: ViewVisibility
  spec: DataViewSpec
  isDefault?: boolean
}

export async function createDataView(input: SaveDataViewInput): Promise<{ id: string }> {
  const defaultForFormId =
    input.isDefault && input.spec.base.kind === 'form' ? input.spec.base.formId : null
  const { data, error } = await supabase
    .from('data_views')
    .insert({
      project_id: input.projectId,
      owner_id: input.ownerId,
      created_by: input.ownerId,
      updated_by: input.ownerId,
      name: input.name,
      description: input.description ?? null,
      visibility: input.visibility,
      spec: input.spec,
      is_default: input.isDefault ?? false,
      default_for_form_id: defaultForFormId,
    })
    .select('id')
    .single()
  if (error) throw error
  return { id: data.id }
}

export async function updateDataView(
  id: string,
  userId: string,
  patch: Partial<Pick<SaveDataViewInput, 'name' | 'description' | 'visibility' | 'spec' | 'isDefault'>>,
): Promise<void> {
  const update: Record<string, unknown> = { updated_by: userId }
  if (patch.name !== undefined) update.name = patch.name
  if (patch.description !== undefined) update.description = patch.description
  if (patch.visibility !== undefined) update.visibility = patch.visibility
  if (patch.spec !== undefined) {
    update.spec = patch.spec
    if (patch.isDefault && patch.spec.base.kind === 'form') {
      update.default_for_form_id = patch.spec.base.formId
    }
  }
  if (patch.isDefault !== undefined) update.is_default = patch.isDefault
  const { error } = await supabase.from('data_views').update(update).eq('id', id)
  if (error) throw error
}

export async function softDeleteDataView(id: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('data_views')
    .update({ is_deleted: true, deleted_at: new Date().toISOString(), deleted_by: userId })
    .eq('id', id)
  if (error) throw error
}

/** Build a fast filter/sort index on a field path (admin only; idempotent). */
export async function ensureFieldValueIndex(formId: string, fieldKey: string): Promise<void> {
  const { error } = await supabase.rpc('ensure_field_value_index', {
    p_form_id: formId,
    p_field_key: fieldKey,
  })
  if (error) throw error
}
